import type { InteractionResolver } from "@repo/app-types/content/structured/parser";
import type {
  ConfigurationOperation,
  SignalOperation,
  TestSignalProposalToolOutput,
} from "@repo/app-types/entities/configurationAgent";
import type { SignalMetadataSchema } from "@repo/app-types/entities/signal";
import { isSignalOperation } from "@repo/app-types/helpers/configurationAgentParser";
import { ChatComposer } from "@repo/ui/chat/chatComposer";
import {
  ChatMessage,
  type ChatMessageRenderTextArgs,
} from "@repo/ui/chat/chatMessage";
import { ChatThread } from "@repo/ui/chat/chatThread";
import { messageItemStyle } from "@repo/ui/chat/messageItem";
import { Size } from "@repo/ui/helpers/helpers";
import { useEffectEvent } from "@repo/ui/helpers/useEffectEvent";
import * as Sentry from "@sentry/react";
import { IconSparkles } from "@tabler/icons-react";
import {
  type FC,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { ConfigurationAgentMessageContent } from "../configurationAssistant/configurationAgentMessageContent.js";
import {
  ConfigurationAssistantContext,
  type ConfigurationAssistantContextValue,
} from "../configurationAssistant/configurationAssistantContext.js";
import { buildInteractionResolverFromMessages } from "../interactionResolver.js";
import { useMastraChatContext } from "../mastra/mastraChatContext.js";
import { FallbackTool } from "../tools/fallbackTool.js";
import { TestSignalProposalTool } from "../tools/testSignalProposalTool.js";
import {
  getSignalAssistantSuggestions,
  type SignalAssistantSuggestion,
} from "./signalAssistantSuggestions.js";
import { SignalEditorStateProvider } from "./signalEditorStateContext.js";
import { SignalOperationCard } from "./signalOperationCard.js";

export interface SignalConfigurationAssistantProps {
  name: string;
  description: string;
  metadataSchema: SignalMetadataSchema;
  onApplyOperation: (operation: SignalOperation) => Promise<void>;
  onTestResultsLoading?: () => void;
  onTestResultsComplete?: (results: TestSignalProposalToolOutput) => void;
}

const TOOL_COMPONENTS = {
  byName: {
    testSignalProposalTool: TestSignalProposalTool,
  },
  Fallback: FallbackTool,
};

export const SignalConfigurationAssistant: FC<
  SignalConfigurationAssistantProps
> = (props) => {
  const {
    name,
    description,
    metadataSchema,
    onTestResultsLoading,
    onTestResultsComplete,
  } = props;
  const chat = useMastraChatContext();
  const suggestions = useMemo(
    () => getSignalAssistantSuggestions({ name, description, metadataSchema }),
    [name, description, metadataSchema],
  );

  // Track which operations have been applied using their stable IDs
  const [appliedOperationIds, setAppliedOperationIds] = useState(
    () => new Set<string>(),
  );

  const onApplyOperation = useEffectEvent(
    async (
      operationId: string,
      operation: ConfigurationOperation,
    ): Promise<void> => {
      if (!isSignalOperation(operation)) {
        throw new Error("Operation type not supported by signal editor");
      }

      if (appliedOperationIds.has(operationId)) {
        return;
      }
      await props.onApplyOperation(operation);
      setAppliedOperationIds((prev) => new Set(prev).add(operationId));
    },
  );

  const isOperationApplied = useCallback(
    (operationId: string) => appliedOperationIds.has(operationId),
    [appliedOperationIds],
  );

  // When the agent iterates (calls the test tool multiple times in one run),
  // we only want to display the final result in the test results pane.
  // We buffer each result in a ref and flush the last one when the run ends.
  const pendingTestResultRef = useRef<TestSignalProposalToolOutput | null>(
    null,
  );
  const isRunning = chat.isRunning;

  // Show loading in the results pane only on the first test call of a run.
  // Subsequent calls keep the pane in its current state (loading) rather
  // than flashing intermediate results.
  const onTestResultsLoadingBuffered = useCallback(() => {
    if (pendingTestResultRef.current === null) {
      onTestResultsLoading?.();
    }
  }, [onTestResultsLoading]);

  // Don't report results immediately — just stash the latest one.
  // Each successive call overwrites the previous, so only the last survives.
  const onTestResultsCompleteBuffered = useCallback(
    (results: TestSignalProposalToolOutput) => {
      pendingTestResultRef.current = results;
    },
    [],
  );

  // When the agent run finishes, flush the buffered result to the pane.
  useEffect(() => {
    if (!isRunning && pendingTestResultRef.current) {
      onTestResultsComplete?.(pendingTestResultRef.current);
      pendingTestResultRef.current = null;
    }
  }, [isRunning, onTestResultsComplete]);

  const contextValue: ConfigurationAssistantContextValue = useMemo(
    () => ({
      applyOperation: onApplyOperation,
      isOperationApplied,
      getEntityName: () => undefined,
      getTestSpec: () => undefined,
      outcomeDefinition: undefined,
      getDocumentDisplayName: () => undefined,
      onTestResultsLoading: onTestResultsLoadingBuffered,
      onTestResultsComplete: onTestResultsCompleteBuffered,
    }),
    [
      onApplyOperation,
      isOperationApplied,
      onTestResultsLoadingBuffered,
      onTestResultsCompleteBuffered,
    ],
  );

  // Quotes and contact references in the agent's messages cite interactions
  // returned by its tool calls, so resolve them against those tool results.
  const resolveInteractionId = useMemo(
    () => buildInteractionResolverFromMessages(chat.messages),
    [chat.messages],
  );

  const lastMessageId = chat.messages.at(-1)?.id;

  return (
    <ConfigurationAssistantContext.Provider value={contextValue}>
      <SignalEditorStateProvider currentDescription={description}>
        <div className="grid min-h-0 w-[350px] flex-auto grid-rows-[1fr_auto] overflow-hidden">
          <ChatThread
            messages={chat.messages}
            isLoading={chat.isLoading}
            emptyState={
              <EmptyState
                suggestions={suggestions}
                onSuggestionClick={chat.sendMessage}
              />
            }
            renderMessage={(message) => (
              <Sentry.ErrorBoundary fallback={<AssistantMessageError />}>
                <ChatMessage
                  message={message}
                  isStreaming={chat.isRunning && message.id === lastMessageId}
                  tools={TOOL_COMPONENTS}
                  renderText={(args) =>
                    renderSignalText(args, resolveInteractionId)
                  }
                />
              </Sentry.ErrorBoundary>
            )}
            composer={
              <div className="border-t border-border-light bg-white p-3">
                <ChatComposer
                  size={Size.SMALL}
                  placeholder="Describe what you want to change..."
                  isRunning={chat.isRunning}
                  onStop={chat.stop}
                  onSend={chat.sendMessage}
                />
              </div>
            }
          />
        </div>
      </SignalEditorStateProvider>
    </ConfigurationAssistantContext.Provider>
  );
};

/** Renders a signal-agent text part, interleaving signal operation cards. */
function renderSignalText(
  { text, role, messageId, isStreaming }: ChatMessageRenderTextArgs,
  resolveInteractionId: InteractionResolver,
) {
  return (
    <ConfigurationAgentMessageContent
      text={text}
      role={role}
      isStreaming={isStreaming}
      resolveInteractionId={resolveInteractionId}
      renderOperation={(segment) => (
        <SignalOperationCard
          operationId={`${messageId}-${segment.id}`}
          operation={segment.operation}
        />
      )}
    />
  );
}

const EmptyState: FC<{
  suggestions: readonly SignalAssistantSuggestion[];
  onSuggestionClick: (prompt: string) => void;
}> = ({ suggestions, onSuggestionClick }) => {
  return (
    <div className="flex flex-1 flex-col px-4 py-3">
      <div className="flex flex-1 flex-col items-center justify-center text-center">
        <div className="flex size-10 items-center justify-center rounded-full bg-indigo-100">
          <IconSparkles className="size-5 text-indigo-600" />
        </div>

        <h3 className="mt-3 text-sm font-semibold text-foreground">
          Configure your signal with AI
        </h3>

        <p className="mt-1 max-w-[260px] text-xs text-muted-foreground">
          I can analyze interactions and help craft effective signal
          definitions. All changes need your approval.
        </p>
      </div>

      <div className="flex flex-wrap gap-2 pb-0">
        {suggestions.map((suggestion) => (
          <SuggestionPill
            key={suggestion.label}
            onClick={() => onSuggestionClick(suggestion.prompt)}
          >
            {suggestion.label}
          </SuggestionPill>
        ))}
      </div>
    </div>
  );
};

const SuggestionPill: FC<{ children: string; onClick: () => void }> = ({
  children,
  onClick,
}) => (
  <button
    type="button"
    onClick={onClick}
    className="rounded-full border border-border-light bg-white px-3 py-1.5 text-left text-xs text-gray-700 shadow-xs transition-colors hover:border-indigo-300 hover:bg-indigo-50 hover:text-indigo-700"
  >
    {children}
  </button>
);

const AssistantMessageError: FC = () => (
  <div
    className={messageItemStyle({
      layout: "agent",
      role: "assistant",
    }).bubble({ className: "bg-red-100 px-3.5" })}
  >
    Unable to render this message.
  </div>
);
